import React, { createContext, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTypedSelector } from '../../../hooks'
import { get, post } from '@fable/api'
import { BlogEvent } from '../../../types'
import { routes } from '../routes'

export interface FeaturedEventState {
  loading: boolean
  error?: Error | null
  data?: any
}

export interface FeaturedEventContextProps {
  event: BlogEvent
  registerUserToEvent: () => void
  checkRegistrationStatus: () => void
  registrationStatus: FeaturedEventState
  registering: FeaturedEventState
}

export const FeaturedEventContext = createContext(
  {} as FeaturedEventContextProps
)

export const FeaturedEventProvider: React.VFC<{
  event: BlogEvent
  children: React.ReactNode
}> = ({ event, children }) => (
  <FeaturedEventContext.Provider value={useFeaturedEventProvider(event)}>
    {children}
  </FeaturedEventContext.Provider>
)

const defaultState = {
  loading: false,
  error: null,
  data: null,
}

export const useFeaturedEventProvider = (event: BlogEvent) => {
  const { profile } = useTypedSelector(({ user }) => user)
  const [registrationStatus, setRegistrationStatus] =
    useState<FeaturedEventState>(defaultState)
  const [registering, setRegistering] =
    useState<FeaturedEventState>(defaultState)
  const navigate = useNavigate()

  const checkRegistrationStatus = async () => {
    if (!event.eventId || event.eventId === '') return
    setRegistrationStatus({ loading: true })

    try {
      const { data } = await get(
        `/event/${event.eventId}/memberships/${profile.data?.id}`
      )
      setRegistrationStatus({ loading: false, data })
    } catch (err) {
      setRegistrationStatus({ loading: false, error: err as Error })
    }
  }

  const registerUserToEvent = async () => {
    if (!event.eventId || event.eventId === '') return
    setRegistering({ loading: true })

    try {
      const { data } = await post(`/event/${event.eventId}/memberships/`)
      setRegistering({ loading: false })

      if (data.status === 'active') {
        navigate(routes.eventRsvpConfirmation, {
          state: { event },
        })
      }
    } catch (err) {
      setRegistering({ loading: false, error: err as Error })
    }
  }

  return {
    event,
    registerUserToEvent,
    checkRegistrationStatus,
    registering,
    registrationStatus,
  }
}

export const useFeaturedEvent = (): FeaturedEventContextProps =>
  useContext(FeaturedEventContext)
